import React, { useState } from "react";
import classes from "./SubscriptionPage.module.css";
import { motion } from "framer-motion";
import {
  FaCheck,
  FaArrowRight,
  FaShieldHalved,
  FaGamepad,
  FaBolt,
} from "react-icons/fa6";
import LinedSection from "../component/LinedSection";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { sendOTPApi } from "../http/http";
import { toast } from "react-toastify";

const PHONE_LENGTH = 8;

const SubscriptionPage = () => {
  const [selectedPlan, setSelectedPlan] = useState(1);
  const [mobile, setMobile] = useState("");
  const [isCaptchaValid, setIsCaptchaValid] = useState(false);
  const [isVerifyingCaptcha, setIsVerifyingCaptcha] = useState(false);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const { t } = useTranslation();

  const isPhoneValid = mobile.length === PHONE_LENGTH;
  const isFormValid = isPhoneValid && isCaptchaValid;

  const handleMobileChange = (e) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, PHONE_LENGTH);
    setMobile(value);
  };

  const handleCaptchaClick = () => {
    if (isCaptchaValid || isVerifyingCaptcha) return;
    setIsVerifyingCaptcha(true);
    setTimeout(() => {
      setIsVerifyingCaptcha(false);
      setIsCaptchaValid(true);
    }, 750);
  };

  const handleConfirm = async (e) => {
    e.preventDefault();

    if (!isFormValid || loading) return;

    const msisdn = `+241${mobile}`;

    try {
      setLoading(true);

      const response = await sendOTPApi(msisdn);
      const result = response?.data;

      if (result?.status === 1) {
        if (result?.message === "Subscription already exist.") {
          toast.info(result.message);
           localStorage.setItem("msisdn", mobile.replace(/\s/g, ""));
           navigate("/home");
          return;
        }

        if (result?.message === "OTP send successfully") {
          toast.success(result.message);

          navigate("/otp", {
            state: {
              mobile: msisdn,
              selectedPlan,
              pack: selectedPlan === 1 ? "daily" : "weekly",
            },
          });

          return;
        }

        toast.info(result?.message || "Unexpected server response.");
      } else {
        toast.error(result?.message || "Failed to send OTP.");
      }
 
} catch (error) {
  const result = error?.response?.data;

  if (result?.message === "Subscription already exist.") {
    toast.info(result.message);

    localStorage.setItem("msisdn", `+241${mobile.replace(/\s/g, "")}`);
    navigate("/home", { replace: true });
    return;
  }

  if (result?.message === "Failed to send otp") {
    toast.error("Failed to send OTP. Please try again.");
  } else if (result?.message === "Internal server error") {
    toast.error("Internal server error. Please try again later.");
  } else {
    toast.error(
      result?.message || "Unable to connect to the server."
    );
  }
} finally {
  setLoading(false);
}

  };

  return (
    <main className={classes.main}>
      <div className={classes.glowLayer} />
      <div className={classes.bgGlowOne} />
      <div className={classes.bgGlowTwo} />

      <LinedSection />

      <motion.section
        className={classes.container}
        initial={{ opacity: 0, y: 22 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
      >
        {/* Top Logo */}
        <div className={classes.topLogoWrap}>
          <img
            src="/images/logo2.png"
            alt="MOOVGAMING"
            className={classes.logoImg}
          />
          <div className={classes.brandSub}>
            Powered by <span>MOOV</span>
          </div>
        </div>

        {/* Heading */}
        <div className={classes.headingWrap}>
          <h1 className={classes.title}>
            {t("34")} <span>{t("35")}</span>
          </h1>
        </div>

        {/* Main Glass Card */}
        <div className={classes.card}>
          {/* Top Switcher (Login / Subscribe) */}
          <div className={classes.tabSwitch}>
            <button
              type="button"
              className={classes.tabBtn}
              onClick={() => navigate("/login")}
            >
              {t("36")}
            </button>

            <button
              type="button"
              className={`${classes.tabBtn} ${classes.tabBtnActive}`}
            >
              {t("37")}
            </button>
          </div>

          <form onSubmit={handleConfirm}>
            {/* Mobile Number Field */}
            <div className={classes.formBlock}>
              <label className={classes.label} htmlFor="mobile-input">
                <span className={classes.labelIcon}><FaBolt /></span>
                {t("38")}
              </label>

              <div className={classes.phoneField}>
                <span className={classes.phoneCode}>
                  <span className={classes.flagDot} title="Gabon (+241)" />
                  +241
                </span>

                <input
                  id="mobile-input"
                  type="tel"
                  inputMode="numeric"
                  autoComplete="tel-national"
                  placeholder="XXXXXXXX"
                  value={mobile}
                  onChange={handleMobileChange}
                  maxLength={PHONE_LENGTH}
                  className={classes.phoneInput}
                  aria-label="Numéro de téléphone Moov à 8 chiffres"
                />
              </div>

              <p className={classes.fieldHint}>
                {t("39")}: {t("150")}
              </p>
            </div>

            {/* Security Verification (reCAPTCHA) */}
            <div className={classes.captchaBlock}>
              <label className={classes.label}>
                <span className={classes.labelIcon}><FaShieldHalved /></span>
                {t("151")}
              </label>

              <div
                className={`${classes.recaptchaCard} ${
                  isCaptchaValid ? classes.recaptchaCardVerified : ""
                }`}
                onClick={handleCaptchaClick}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleCaptchaClick();
                  }
                }}
                aria-label="reCAPTCHA verification"
              >
                <div className={classes.recaptchaLeft}>
                  <div
                    className={`${classes.recaptchaCheckbox} ${
                      isCaptchaValid ? classes.recaptchaCheckboxChecked : ""
                    } ${isVerifyingCaptcha ? classes.recaptchaCheckboxLoading : ""}`}
                  >
                    {isVerifyingCaptcha && (
                      <div className={classes.recaptchaSpinner} />
                    )}

                    {isCaptchaValid && (
                      <motion.svg
                        viewBox="0 0 24 24"
                        className={classes.recaptchaCheckSvg}
                        initial={{ scale: 0.2, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ duration: 0.2, ease: "easeOut" }}
                      >
                        <path
                          d="M4.5 12.75l6 6 9-13.5"
                          fill="none"
                          stroke="#10B981"
                          strokeWidth="3.4"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </motion.svg>
                    )}
                  </div>

                  <span className={classes.recaptchaLabel}>
                    {t("168")}
                  </span>
                </div>

                <div className={classes.recaptchaBrand}>
                  <div className={classes.recaptchaLogoWrap}>
                    <svg
                      width="26"
                      height="26"
                      viewBox="0 0 48 48"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className={classes.recaptchaLogoSvg}
                    >
                      <path
                        d="M24 5C34.5 5 43 13.5 43 24C43 27.2 42.2 30.2 40.8 32.8L36.2 30.2C37.3 28.3 38 26.2 38 24C38 16.3 31.7 10 24 10C20.2 10 16.8 11.5 14.3 14L18 18H7V7L10.8 10.8C14.1 7.2 18.8 5 24 5Z"
                        fill="#0066FF"
                      />
                      <path
                        d="M24 43C13.5 43 5 34.5 5 24C5 20.8 5.8 17.8 7.2 15.2L11.8 17.8C10.7 19.7 10 21.8 10 24C10 31.7 16.3 38 24 38C27.8 38 31.2 36.5 33.7 34L30 30H41V41L37.2 37.2C33.9 40.8 29.2 43 24 43Z"
                        fill="#64748B"
                      />
                    </svg>
                  </div>
                  <span className={classes.recaptchaBrandName}>reCAPTCHA</span>
                  <div className={classes.recaptchaLinks}>
                    <a
                      href="https://www.google.com/intl/fr/policies/privacy/"
                      target="_blank"
                      rel="noreferrer"
                      className={classes.recaptchaLink}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {t("169")}
                    </a>
                    <span className={classes.recaptchaLinkDivider}>-</span>
                    <a
                      href="https://www.google.com/intl/fr/policies/terms/"
                      target="_blank"
                      rel="noreferrer"
                      className={classes.recaptchaLink}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {t("170")}
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Subscription Plans Header */}
            <div className={classes.plansHeader}>
              <span className={classes.plansTitle}>
                <span className={classes.labelIcon}><FaGamepad /></span>
                {t("12")}
              </span>
            </div>

            {/* Subscription Plan Cards */}
            <div className={classes.plansWrap}>
              {/* Plan 1: Daily */}
              <motion.button
                type="button"
                className={`${classes.planCard} ${selectedPlan === 1 ? classes.planCardActive : ""
                  }`}
                onClick={() => setSelectedPlan(1)}
                whileHover={{ scale: 1.015 }}
                whileTap={{ scale: 0.985 }}
              >
                <div className={classes.planLeft}>
                  <div className={classes.planIconWrap}>
                    <img
                      src="/images/game-controller.png"
                      alt="Game"
                      className={classes.planIcon}
                    />
                  </div>

                  <div className={classes.planInfo}>
                    <div
                      className={`${classes.planName} ${selectedPlan === 1 ? classes.planNameActive : ""
                        }`}
                    >
                      {t("40")}
                    </div>
                    <div
                      className={`${classes.planDesc} ${selectedPlan === 1 ? classes.planDescActive : ""
                        }`}
                    >
                      {t("41")}
                    </div>
                  </div>
                </div>

                <div className={classes.planRight}>
                  <div
                    className={`${classes.planPrice} ${selectedPlan === 1 ? classes.planPriceActive : ""
                      }`}
                  >
                    100<span className={classes.planPriceUnit}>CFA</span>
                  </div>

                  <div
                    className={`${classes.selector} ${selectedPlan === 1 ? classes.selectorActive : ""
                      }`}
                  >
                    {selectedPlan === 1 && <FaCheck />}
                  </div>
                </div>
              </motion.button>

              {/* Plan 2: Weekly (Most Popular) */}
              <motion.button
                type="button"
                className={`${classes.planCard} ${selectedPlan === 2 ? classes.planCardActive : ""
                  }`}
                onClick={() => setSelectedPlan(2)}
                whileHover={{ scale: 1.015 }}
                whileTap={{ scale: 0.985 }}
              >
                <span className={classes.popularBadge}>
                  {t("15")}
                </span>

                <div className={classes.planLeft}>
                  <div className={classes.planIconWrap}>
                    <img
                      src="/images/schedule.png"
                      alt="Schedule"
                      className={classes.planIcon}
                    />
                  </div>

                  <div className={classes.planInfo}>
                    <div
                      className={`${classes.planName} ${selectedPlan === 2 ? classes.planNameActive : ""
                        }`}
                    >
                      {t("42")}
                    </div>
                    <div
                      className={`${classes.planDesc} ${selectedPlan === 2 ? classes.planDescActive : ""
                        }`}
                    >
                      {t("43")}
                    </div>
                  </div>
                </div>

                <div className={classes.planRight}>
                  <div
                    className={`${classes.planPrice} ${selectedPlan === 2 ? classes.planPriceActive : ""
                      }`}
                  >
                    500<span className={classes.planPriceUnit}>CFA</span>
                  </div>

                  <div
                    className={`${classes.selector} ${selectedPlan === 2 ? classes.selectorActive : ""
                      }`}
                  >
                    {selectedPlan === 2 && <FaCheck />}
                  </div>
                </div>
              </motion.button>
            </div>

            {/* Confirm CTA Button */}
            <motion.button
              type="submit"
              className={`${classes.confirmBtn} ${!isFormValid || loading ? classes.confirmBtnDisabled : ""
                }`}
              disabled={!isFormValid || loading}
              whileHover={isFormValid && !loading ? { scale: 1.02 } : {}}
              whileTap={isFormValid && !loading ? { scale: 0.98 } : {}}
            >
              <span>
                {loading ? t("155") : t("46")}
              </span>
              {!loading && <FaArrowRight />}
            </motion.button>
          </form>
        </div>

        {/* Footer */}
        <div className={classes.footerText}>
          &copy; UVCANN SOLUTIONS LLP &bull; {t("156")}
        </div>
      </motion.section>
    </main>
  );
};

export default SubscriptionPage;